export type Category = 'top' | 'bottom' | 'dress' | 'outerwear' | 'shoes' | 'accessory' | 'other';
export type Presence = 'inStorage' | 'outOfStorage' | 'unknown';
export type CareState = 'clean' | 'rewearable' | 'dirty' | 'washing' | 'drying';
export type Occasion = 'work' | 'casual' | 'dinner' | 'formal' | 'exercise';
export type Mood = 'relaxed' | 'balanced' | 'polished' | 'bold';
export type Weather = 'warm' | 'mild' | 'cool' | 'cold' | 'rainy';

export type Garment = {
  id: string; name: string; category: Category; color: string; brand: string; material: string;
  seasons: string[]; styleTags: string[]; formality: number; warmth: number; favorite: boolean;
  presence: Presence; careState: CareState; locationId: string | null; image?: string;
  wearCount: number; lastWornAt?: string; createdAt: string; updatedAt: string;
};
export type Location = { id: string; name: string; type: string; createdAt: string };
export type Outfit = { id: string; name: string; garmentIds: string[]; occasion: Occasion; mood: Mood; origin: 'manual' | 'recommended'; wearCount: number; updatedAt: string };
export type WardrobeEvent = { id: string; garmentId: string; type: string; at: string; details?: string };
export type WardrobeState = { version: 1; workspaceId: string; garments: Garment[]; locations: Location[]; outfits: Outfit[]; events: WardrobeEvent[] };
export type Recommendation = { id: string; garmentIds: string[]; score: number; explanation: string };

export const wardrobeStorageKey = 'prapti-wardrobe-v1';
export const newId = () => globalThis.crypto?.randomUUID?.() ?? `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
export const emptyWardrobe = (): WardrobeState => ({ version: 1, workspaceId: newId(), garments: [], locations: [], outfits: [], events: [] });
export const available = (g: Garment) => g.presence === 'inStorage' && Boolean(g.locationId) && (g.careState === 'clean' || g.careState === 'rewearable');

export function loadWardrobe(): WardrobeState {
  if (typeof window === 'undefined') return emptyWardrobe();
  try {
    const raw = window.localStorage.getItem(wardrobeStorageKey);
    if (!raw) return emptyWardrobe();
    const parsed = JSON.parse(raw) as WardrobeState;
    return parsed.version === 1 && Array.isArray(parsed.garments) ? parsed : emptyWardrobe();
  } catch { return emptyWardrobe(); }
}

export function recommend(garments: Garment[], occasion: Occasion, mood: Mood, weather: Weather, anchorId?: string): Recommendation[] {
  const pool = garments.filter(available).filter(g => weather !== 'warm' || g.warmth <= 3).filter(g => weather !== 'cold' || g.warmth >= 2);
  if (anchorId && !pool.some(g => g.id === anchorId)) return [];
  const tops = pool.filter(g => g.category === 'top'); const bottoms = pool.filter(g => g.category === 'bottom');
  const dresses = pool.filter(g => g.category === 'dress'); const shoes = pool.filter(g => g.category === 'shoes');
  const outerwear = pool.filter(g => g.category === 'outerwear');
  const sets: Garment[][] = [];
  tops.forEach(top => bottoms.forEach(bottom => shoes.forEach(shoe => sets.push([top, bottom, shoe]))));
  dresses.forEach(dress => shoes.forEach(shoe => sets.push([dress, shoe])));
  const withOuterwear = sets.map(set => weather === 'cool' || weather === 'cold' || weather === 'rainy' ? [...set, ...outerwear.slice(0, 1)] : set);
  const target = ({ exercise: 1, casual: 2, work: 4, dinner: 4, formal: 5 } as const)[occasion];
  return withOuterwear.filter(set => !anchorId || set.some(g => g.id === anchorId)).map(set => {
    const avg = set.reduce((sum, g) => sum + g.formality, 0) / set.length;
    const styleMatches = set.filter(g => g.styleTags.includes(mood)).length;
    const recentPenalty = set.filter(g => g.lastWornAt && Date.now() - Date.parse(g.lastWornAt) < 172800000).length;
    const score = 100 - Math.round(Math.abs(avg - target) * 12) + styleMatches * 7 - recentPenalty * 6;
    const reasons = [`fits a ${occasion} day`, `uses ${set.length} available pieces`];
    if (styleMatches) reasons.push(`matches your ${mood} mood`);
    if (weather === 'cold' || weather === 'rainy') reasons.push(`adds weather-ready warmth`);
    return { id: set.map(g => g.id).sort().join('-'), garmentIds: set.map(g => g.id), score, explanation: reasons.join(' · ') };
  }).sort((a, b) => b.score - a.score || a.id.localeCompare(b.id)).filter((item, index, all) => index === 0 || item.garmentIds.filter(id => all[index - 1].garmentIds.includes(id)).length < item.garmentIds.length - 1).slice(0, 3);
}

export function createGarment(input: Pick<Garment, 'name' | 'category' | 'color'> & Partial<Garment>): Garment {
  const now = new Date().toISOString();
  return { id: newId(), name: input.name.trim(), category: input.category, color: input.color, brand: '', material: '', seasons: ['all'], styleTags: [], formality: 3, warmth: 3, favorite: false, presence: 'unknown', careState: 'clean', locationId: null, wearCount: 0, createdAt: now, updatedAt: now, ...input };
}

export function sampleWardrobe(): WardrobeState {
  const createdAt = new Date().toISOString();
  const railId = newId();
  const drawerId = newId();
  const shoeRackId = newId();
  const make = (name: string, category: Category, color: string, locationId: string | null, extras: Partial<Garment> = {}) =>
    createGarment({ name, category, color, locationId, presence: locationId ? 'inStorage' : 'outOfStorage', ...extras });
  const garments = [
    make('Ivory silk blouse', 'top', 'ivory', railId, { brand: 'Sezane', material: 'silk', formality: 4, warmth: 2, styleTags: ['polished'] }),
    make('Navy ribbed tee', 'top', 'navy', drawerId, { brand: 'COS', material: 'cotton', formality: 2, warmth: 2, styleTags: ['balanced'] }),
    make('Emerald knit', 'top', 'green', drawerId, { material: 'merino wool', formality: 3, warmth: 4, styleTags: ['bold'] }),
    make('Tailored black trousers', 'bottom', 'black', railId, { material: 'wool blend', formality: 5, warmth: 3, styleTags: ['polished'] }),
    make('Vintage straight jeans', 'bottom', 'blue', railId, { material: 'denim', formality: 2, warmth: 3, styleTags: ['relaxed'] }),
    make('Satin midi skirt', 'bottom', 'burgundy', railId, { formality: 4, warmth: 2, styleTags: ['bold'] }),
    make('Midnight wrap dress', 'dress', 'midnight blue', railId, { formality: 4, warmth: 2, styleTags: ['polished'] }),
    make('Camel trench coat', 'outerwear', 'camel', railId, { material: 'cotton', formality: 4, warmth: 3, styleTags: ['balanced'] }),
    make('White leather trainers', 'shoes', 'white', shoeRackId, { formality: 2, warmth: 2, styleTags: ['relaxed'] }),
    make('Black ankle boots', 'shoes', 'black', shoeRackId, { formality: 4, warmth: 4, styleTags: ['polished'] }),
    make('Running leggings', 'bottom', 'charcoal', null, { brand: 'Lululemon', material: 'technical jersey', formality: 1, warmth: 2, careState: 'dirty', styleTags: ['relaxed'] }),
    make('Statement earrings', 'accessory', 'gold', drawerId, { formality: 4, warmth: 1, styleTags: ['bold'] }),
  ];
  const byName = (name: string) => garments.find(item => item.name === name)!.id;
  return {
    version: 1,
    workspaceId: newId(),
    locations: [
      { id: railId, name: 'Main wardrobe rail', type: 'wardrobe', createdAt },
      { id: drawerId, name: 'Top drawer', type: 'drawer', createdAt },
      { id: shoeRackId, name: 'Shoe rack', type: 'rack', createdAt },
    ],
    garments,
    outfits: [
      { id: newId(), name: 'Confident Monday', garmentIds: [byName('Ivory silk blouse'), byName('Tailored black trousers'), byName('Black ankle boots')], occasion: 'work', mood: 'polished', origin: 'manual', wearCount: 2, updatedAt: createdAt },
      { id: newId(), name: 'Easy weekend', garmentIds: [byName('Navy ribbed tee'), byName('Vintage straight jeans'), byName('White leather trainers')], occasion: 'casual', mood: 'relaxed', origin: 'manual', wearCount: 4, updatedAt: createdAt },
    ],
    events: [],
  };
}
